;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();import { OfferInfo } from '@qogita/canary-client/sellers'
import { cn } from '@qogita/ui/utils/cn'
import { Anchor } from '@qogita/ui/v2/anchor'
import { Icon } from '@qogita/ui/v2/icon'
import {
  ScrollableTable as Table,
  TableTBody,
  TableTd,
  TableTh,
  TableTHead,
  TableTr,
} from '@qogita/ui/v2/scrollable-table'
import clsx from 'clsx'
import { ComponentProps } from 'react'
import { EMAILS } from 'src/core/constants/copy'
import { ErrorBoundary } from 'src/deprecated/components/shared/ErrorBoundary'
import { LoadingDots } from 'src/deprecated/components/shared/LoadingDots'
import { Pagination } from 'src/deprecated/components/shared/Pagination'
import { PAGE_SIZE } from 'src/deprecated/constants'
import { useInventoryQueryParams } from 'src/deprecated/hooks/utils/useInventoryQueryParams'
import { getFormattedCurrencyWithSymbol } from 'src/deprecated/utils/getFormattedCurrency'
import { getFormattedNumber } from 'src/deprecated/utils/getFormattedNumber'
import { trackInventoryFiltered } from 'src/deprecated/utils/report/tracking'

import { ProductName } from '../../../orders.[qid]/_components/ConfirmQuantitiesStep/ProductName'

class InvalidOfferPriceError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidOfferPriceError'
  }
}

const OfferPriceCell = ({ offer }: { offer: OfferInfo }) => {
  const originalPrice = Number(offer.originalPrice)

  if (isNaN(originalPrice)) {
    // "offer.originalPrice" type can be null, but there is not a single entry with null value in the database,
    // either way, we have this check to prevent showing NaN in the UI, and to bubble up the error to sentry.
    throw new InvalidOfferPriceError(
      `The offer "${offer.qid}" is expected to have a valid string number for "offer.originalPrice", but "${offer.originalPrice}" was received instead.`,
    )
  }

  /*
    Using originalPrice and originalPriceCurrency ensures that we show the correct currency for the seller. Price and currency are always in the qogita currency that is EUR.
  */
  const formattedUnitPrice = getFormattedCurrencyWithSymbol(
    originalPrice,
    offer.originalPriceCurrency || undefined,
  )

  return <span className="text-base text-gray-700">{formattedUnitPrice}</span>
}

const InventoryTableTh = ({
  className,
  ...rest
}: ComponentProps<typeof TableTh>) => {
  // oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component
  return <TableTh {...rest} className={cn('px-4', className)} />
}

const InventoryTableTd = ({
  className,
  ...rest
}: ComponentProps<typeof TableTd>) => {
  // oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component
  return <TableTd {...rest} className={cn('px-4', className)} />
}

type InventoryTableProps = {
  offers: OfferInfo[]
  totalResults: number
  isLoading: boolean
  isPlaceholderData: boolean
}
export const InventoryTable = ({
  offers,
  totalResults,
  isLoading,
  isPlaceholderData,
}: InventoryTableProps) => {
  const hasOffers = offers.length > 0
  const isLoadingState = !hasOffers && isLoading
  const isEmptyState = !hasOffers && !isLoading

  const { page, sort, setSort, brandsFilter } = useInventoryQueryParams()

  const isInventoryFiltered = brandsFilter.length > 0

  const setSortWithTracking: typeof setSort = (sort) => {
    trackInventoryFiltered({
      type: 'sort',
      sort,
      source: 'inventoryTable',
    })
    setSort(sort)
  }

  return (
    <div className="overflow-scroll rounded-lg rounded-t-none border border-gray-300">
      <Table>
        <TableTHead>
          <TableTr>
            <InventoryTableTh
              // oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component
              className="bg-white sm:sticky sm:left-0 sm:z-10"
              align="left"
            >
              Product&nbsp;details
            </InventoryTableTh>
            <InventoryTableTh
              align="right"
              onClick={() =>
                setSortWithTracking(
                  sort === '-inventory' ? 'inventory' : '-inventory',
                )
              }
              sort={
                sort === 'inventory'
                  ? 'ascending'
                  : sort === '-inventory'
                    ? 'descending'
                    : undefined
              }
            >
              Quantity
            </InventoryTableTh>
            <InventoryTableTh
              align="right"
              onClick={() =>
                setSortWithTracking(
                  sort === '-originalPrice'
                    ? 'originalPrice'
                    : '-originalPrice',
                )
              }
              sort={
                sort === 'originalPrice'
                  ? 'ascending'
                  : sort === '-originalPrice'
                    ? 'descending'
                    : undefined
              }
            >
              Unit&nbsp;price
            </InventoryTableTh>
          </TableTr>
        </TableTHead>
        {/* oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component */}
        <TableTBody className={clsx({ 'opacity-50': isPlaceholderData })}>
          {offers.map((offer) => (
            <TableTr key={offer.qid}>
              {/* oxlint-disable-next-line shadcn/no-restyle -- grandfathered: pre-rollout restyle className on v2 component */}
              <InventoryTableTd className="w-[40%] bg-inherit text-left sm:sticky sm:left-0 sm:bg-clip-padding">
                <ProductName
                  imageUrl={offer.variant.images[0]?.url || ''}
                  gtin={offer.variant.gtin}
                  gtinText={offer.variant.gtin}
                  linkText={offer.variant.name}
                  sellingUnit={offer.unit}
                />
              </InventoryTableTd>

              <InventoryTableTd className="md:text-right">
                <span
                  className={clsx('text-base text-gray-700', {
                    'text-error-500 group-hover:text-error-700':
                      !offer.inventory,
                  })}
                >
                  {getFormattedNumber(offer.inventory, 0)}
                </span>
              </InventoryTableTd>
              <InventoryTableTd className="md:text-right">
                <ErrorBoundary fallback={null}>
                  <OfferPriceCell offer={offer} />
                </ErrorBoundary>
              </InventoryTableTd>
            </TableTr>
          ))}
        </TableTBody>
      </Table>
      {isLoadingState ? (
        <div className="flex justify-center py-40">
          <LoadingDots rebranded />
        </div>
      ) : null}

      {isEmptyState ? (
        <div className="my-24 flex grow flex-col items-center justify-center gap-3 text-sm">
          <Icon name="heroicons/solid/magnifying-glass" className="size-10" />
          <h3 className="font-figtree text-3xl font-semibold text-gray-900">
            No products found
          </h3>

          {isInventoryFiltered ? (
            <p className="text-sm text-gray-500">
              No results meet this criteria, try to adjust your filters.
            </p>
          ) : null}

          <p className="flex flex-col text-gray-500">
            If something doesn&apos;t look right,
            <Anchor
              href={`mailto:${EMAILS.success}`}
              size="inline"
              variant="inline-text"
            >
              email our support team
            </Anchor>
          </p>
        </div>
      ) : null}

      {!isLoadingState && !isEmptyState ? (
        <div className="px-4 py-6 empty:hidden">
          <Pagination
            rebranded
            currentPage={page}
            pageSize={PAGE_SIZE}
            resultsLabel="product"
            resultsCount={totalResults}
          />
        </div>
      ) : null}
    </div>
  )
}
